import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getMonthData } from '../../../../utils/calendar/dateUtils';
import { useRoutines } from '../../../../hooks/useRoutines';
import type { DayData } from '../../../../types/calendar';

export function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { routines } = useRoutines(currentDate);

  const monthData = getMonthData(currentDate);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  return (
    <div className="bg-background-card rounded-xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-bold text-black">
          {monthData.monthName} {currentDate.getFullYear()}
        </h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 text-black hover:text-accent-green transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 text-black hover:text-accent-green transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-black py-2"
          >
            {day}
          </div>
        ))}
        
        {monthData.days.map((day: DayData, index: number) => (
          <div
            key={index}
            className={`
              aspect-square p-2 text-sm rounded-lg transition-colors
              ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
              ${isToday(day.date) ? 'ring-2 ring-accent-green' : ''}
              ${day.hasCompletedRoutines ? 'bg-accent-green/10' : ''}
            `}
          >
            <span className={`
              block text-center
              ${day.isCurrentMonth ? 'text-black' : 'text-gray-400'}
              ${isToday(day.date) ? 'font-bold' : ''}
            `}>
              {day.date.getDate()}
            </span>
            {day.hasCompletedRoutines && (
              <div className="mt-1 flex justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-green"></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {routines.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-800">
          <h3 className="text-sm font-medium text-black mb-4">Today's Routines</h3>
          <div className="space-y-2">
            {routines.map((routine) => (
              <div
                key={routine.id}
                className="flex items-center justify-between p-2 rounded-lg bg-white"
              >
                <span className="text-sm text-black">{routine.title}</span>
                <input
                  type="checkbox"
                  checked={routine.completed}
                  onChange={() => {}}
                  className="rounded border-gray-300 text-accent-green focus:ring-accent-green"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}